import {catchError, mergeMap, shareReplay, tap, retryWhen} from 'rxjs/operators';
import {from, identity} from 'rxjs';

import setupI18n from 'web-app/setup/i18next-setup';
import setupLocale, {getExpectedLocale} from 'web-app/setup/locale-setup';
import ENV from 'web-app/config/environment';
import {loadLoginScreenTranslationsFirst} from 'web-app/setup/load-login-screen-translations-first';
import {DEFAULT_NAMESPACE} from 'web-app/setup/translations-load-path';
import {captureMessage} from 'web-app/services/sentry';
import {bootsIntoLoginScreen} from 'web-app/react/routes/application-route-helpers/boots-into-login-screen';
import PlatformHelper from 'web-app/helpers/platform-helper';
import {genericRetryStrategy} from 'web-app/util/rxjs';
import {getI18nextLanguage} from 'web-app/util/locale';
import translationFallbackLanguages from 'web-app/setup/translation-fallback-languages.json';

/**
 * Currently this file is imported both by Ember's application route, AND
 * by the React.Router application route. To avoid cyclical imports, and/or changing the current
 * import order, this code has to live in its own file as long as both implementations exist alongside eachother.
 */

const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 500;

// Booting in the language `setupLocale` is about to switch to spares a download of the base catalogue.
const fetchTranslationObservable$ = (version: string) =>
    from(
        setupI18n(`locales/{{lng}}-${version}.json`, {
            eagerNamespaces: bootsIntoLoginScreen ? [] : [DEFAULT_NAMESPACE],
            language: getI18nextLanguage(getExpectedLocale()),
            completedLanguages: Object.keys(translationFallbackLanguages),
        }).then(async t => {
            if (bootsIntoLoginScreen) {
                await loadLoginScreenTranslationsFirst();
            }
            return t;
        }),
    );

/**
 * Fetch translations with retry and linear backoff.
 * Retries help handle temporary unavailability during deployments when
 * translation files may not have propagated to CDN edges yet.
 */
const fetchTranslationWithRetry$ = (version: string) =>
    fetchTranslationObservable$(version).pipe(
        retryWhen(
            genericRetryStrategy({
                maxRetryAttempts: MAX_RETRIES,
                scalingDuration: RETRY_DELAY_MS,
            }),
        ),
    );

/**
 * Attempt to fetch translations based on baked-in version.
 * If we can't fetch translations based on baked-in version (after retries),
 * we fetch the latest version via /release.txt and use it to get the latest translations
 */
const fetchTranslationsWithFallback$ = fetchTranslationWithRetry$(encodeURIComponent(ENV.APP.version)).pipe(
    // Don't use fallback strategy on mobile apps
    PlatformHelper.isMobileApp()
        ? identity
        : catchError(() =>
              // Ideally we would only fall back on 404, but i18next-xhr-backend doesn't forward the network error
              from(fetch(ENV.releaseUrl)).pipe(
                  mergeMap(response => response.text()),
                  tap(version => {
                      captureMessage('Unable to fetch translation files', {
                          extra: {
                              failedVersion: ENV.APP.version,
                              fallbackVersion: version,
                          },
                      });
                  }),
                  mergeMap(version => fetchTranslationWithRetry$(version)),
              ),
          ),
);

// Perform async i18next setup
export const withTranslationsReady$ = fetchTranslationsWithFallback$.pipe(
    // The login boot has already applied the locale, before loading the login screen's namespace.
    tap(() => {
        if (!bootsIntoLoginScreen) {
            setupLocale();
        }
    }),
    shareReplay(),
);
